import * as React from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { z } from 'zod'
import type { Thrown } from '@lib/helpers/thrown'
import { AuthLayout, Button, Field, Input } from '@app/ui'
import { useAuth } from '@/auth/AuthContext'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { loginInputSchema } from '@/schemas/auth.schema'

/** In-app path only. Rejects protocol-relative and absolute URLs. */
function isInAppPath(path: string): boolean {
  return (
    path.startsWith('/') && !path.startsWith('//') && !path.includes('\\')
  )
}

const loginRedirectStateSchema = z.object({
  from: z.string().refine(isInAppPath),
})

/**
 * Read the post-login redirect from router state.
 * `useLocation().state` is untyped history state, so it is parsed here.
 */
function resolveRedirect(
  state: Parameters<typeof loginRedirectStateSchema.safeParse>[0],
): string {
  const parsed = loginRedirectStateSchema.safeParse(state)
  return parsed.success ? parsed.data.from : '/'
}

/** Turn an API/network failure into a message suitable for the sign-in form. */
function loginErrorMessage(error: Thrown): string {
  if (isApiError(error)) {
    if (error.status === 401) return 'Incorrect email or password.'
    return error.body.message ?? 'Unable to sign in. Please try again.'
  }
  return toErrorMessage(error)
}

/** Email + password sign-in page. Redirects to the attempted route on success. */
export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [fieldError, setFieldError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setFieldError(null)
    setFormError(null)

    const parsed = loginInputSchema.safeParse({ email, password })
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Invalid credentials')
      return
    }

    setIsSubmitting(true)
    try {
      await login(parsed.data)
      navigate(resolveRedirect(location.state), { replace: true })
    } catch (error) {
      setFormError(loginErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Enter your credentials to access your workspace."
      variant="card"
    >
      <form className="space-y-4" onSubmit={handleSubmit} noValidate>
        <Field label="Email" htmlFor="email" error={fieldError ?? undefined}>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            invalid={fieldError != null}
          />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <div className="text-right">
          <Link
            to="/forgot-password"
            className="text-ds-sm text-ds-brand hover:underline"
          >
            Forgot your password?
          </Link>
        </div>

        {formError ? (
          <p className="text-ds-sm text-ds-danger" role="alert">
            {formError}
          </p>
        ) : null}

        <Button type="submit" variant="primary" className="w-full" loading={isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthLayout>
  )
}
